package com.ijoin.ui.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.ijoin.data.api.FeedApi
import com.ijoin.data.model.Event
import com.ijoin.data.model.FeedItem
import com.ijoin.data.model.Ticket
import com.ijoin.data.repository.ChatRepository
import com.ijoin.data.repository.EventRepository
import com.ijoin.data.repository.TicketRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class HomeViewModel(
    private val eventRepository: EventRepository = EventRepository(),
    private val ticketRepository: TicketRepository = TicketRepository(),
    private val feedApi: FeedApi = FeedApi(),
    private val chatRepository: ChatRepository = ChatRepository(),
) : ViewModel() {

    private val FEED_PAGE_SIZE = 6

    private val _uiState = MutableStateFlow(HomeUiState())
    val uiState: StateFlow<HomeUiState> = _uiState.asStateFlow()

    // Pagination state for feed
    private var currentFeedPage = 0
    private var isLoadingMore = false
    private var hasMoreFeed = true
    private var hasLoadedOnce = false
    private var lastAuthState: Boolean? = null

    fun loadData(forceRefresh: Boolean = false, isAuthenticated: Boolean = true) {
        if (!forceRefresh && (_uiState.value.isLoading || (hasLoadedOnce && lastAuthState == isAuthenticated))) {
            return
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            currentFeedPage = 0
            hasMoreFeed = true
            lastAuthState = isAuthenticated

            val upcomingDeferred = if (isAuthenticated) {
                async { ticketRepository.getUpcomingTickets() }
            } else {
                null
            }
            val favoritesDeferred = if (isAuthenticated) {
                async { eventRepository.getFavoriteEvents() }
            } else {
                null
            }
            val feedDeferred = async { feedApi.getUserFeed(page = 0, size = FEED_PAGE_SIZE) }
            val chatsDeferred = if (isAuthenticated) {
                async { chatRepository.getChats() }
            } else {
                null
            }

            var firstError: String? = null

            if (upcomingDeferred != null && favoritesDeferred != null && chatsDeferred != null) {
                upcomingDeferred.await()
                    .onSuccess { tickets ->
                        _uiState.update { it.copy(upcomingTickets = tickets) }
                    }
                    .onFailure { error ->
                        if (firstError == null) firstError = error.message
                    }

                favoritesDeferred.await()
                    .onSuccess { events ->
                        _uiState.update { it.copy(favoriteEvents = events) }
                    }
                    .onFailure { error ->
                        if (firstError == null) firstError = error.message
                    }
            } else {
                _uiState.update {
                    it.copy(
                        upcomingTickets = emptyList(),
                        favoriteEvents = emptyList(),
                        hasUnreadMessages = false
                    )
                }
            }

            feedDeferred.await()
                .onSuccess { feedItems ->
                    hasMoreFeed = feedItems.size >= FEED_PAGE_SIZE
                    _uiState.update {
                        it.copy(
                            feedItems = feedItems,
                            isLoading = false,
                            error = firstError
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = firstError ?: error.message
                        )
                    }
                }

            if (chatsDeferred != null) {
                chatsDeferred.await()
                    .onSuccess { chats ->
                        val hasUnread = chats.any { it.unreadCount > 0 }
                        _uiState.update { it.copy(hasUnreadMessages = hasUnread) }
                    }
                    .onFailure { error ->
                        _uiState.update { it.copy(error = error.message) }
                    }
            }

            hasLoadedOnce = true
        }
    }

    fun refreshUnreadChats() {
        viewModelScope.launch {
            chatRepository.getChats()
                .onSuccess { chats ->
                    val hasUnread = chats.any { it.unreadCount > 0 }
                    _uiState.update { it.copy(hasUnreadMessages = hasUnread) }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(error = error.message) }
                }
        }
    }

    fun markChatsOpened() {
        _uiState.update { it.copy(hasUnreadMessages = false) }
    }

    fun loadMoreFeed() {
        if (isLoadingMore || !hasMoreFeed) return

        viewModelScope.launch {
            isLoadingMore = true
            _uiState.update { it.copy(isLoadingMore = true) }

            currentFeedPage++

            feedApi.getUserFeed(page = currentFeedPage, size = FEED_PAGE_SIZE)
                .onSuccess { feedItems ->
                    val updatedFeed = _uiState.value.feedItems + feedItems
                    _uiState.update {
                        it.copy(
                            feedItems = updatedFeed,
                            isLoadingMore = false,
                            error = null
                        )
                    }
                    hasMoreFeed = feedItems.size >= 10
                    isLoadingMore = false
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            error = error.message,
                            isLoadingMore = false
                        )
                    }
                    isLoadingMore = false
                }
        }
    }

    fun searchEvents(query: String) {
        if (query.isBlank()) {
            _uiState.update { it.copy(searchResults = null, error = null) }
            return
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isSearching = true, error = null) }

            eventRepository.searchEvents(query)
                .onSuccess { events ->
                    _uiState.update {
                        it.copy(
                            searchResults = events,
                            isSearching = false
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            error = error.message,
                            isSearching = false
                        )
                    }
                }
        }
    }

    fun clearSearch() {
        _uiState.update { it.copy(searchResults = null) }
    }

    fun toggleFavorite(eventId: String) {
        viewModelScope.launch {
            val isFavorite = _uiState.value.favoriteEvents.any { it.id == eventId }
            eventRepository.toggleFavorite(eventId, isFavorite)
                .onSuccess {
                    if (isFavorite) {
                        _uiState.update {
                            it.copy(
                                favoriteEvents = it.favoriteEvents.filterNot { event -> event.id == eventId }
                            )
                        }
                    } else {
                        eventRepository.getEventById(eventId)
                            .onSuccess { event ->
                                _uiState.update {
                                    val updatedFavorites = (it.favoriteEvents + event)
                                        .distinctBy { fav -> fav.id }
                                    it.copy(favoriteEvents = updatedFavorites)
                                }
                            }
                            .onFailure { error ->
                                _uiState.update { it.copy(error = error.message) }
                            }
                    }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(error = error.message) }
                }
        }
    }

    fun toggleTicketFavorite(ticketId: String) {
        viewModelScope.launch {
            ticketRepository.toggleFavorite(ticketId)
                .onSuccess { updatedTicket ->
                    _uiState.update {
                        val updatedTickets = it.upcomingTickets.map { ticket ->
                            if (ticket.id == ticketId) updatedTicket else ticket
                        }
                        it.copy(upcomingTickets = updatedTickets, error = null)
                    }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(error = error.message) }
                }
        }
    }
}

data class HomeUiState(
    val isLoading: Boolean = false,
    val isLoadingMore: Boolean = false,
    val isSearching: Boolean = false,
    val upcomingTickets: List<Ticket> = emptyList(),
    val favoriteEvents: List<Event> = emptyList(),
    val feedItems: List<FeedItem> = emptyList(),
    val searchResults: List<Event>? = null,
    val error: String? = null,
    val hasUnreadMessages: Boolean = false,
)
